import { Collapse, Nav } from '@wyz/ui';
import clsx from 'clsx';
import { isNil, isObject } from 'lodash-es';
import { Fragment, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useTheme, Trans } from '~/common/context';
import {
  RouteDefinition,
  SeparatorDefinition,
  SideBarRoute,
  TranslationKeys,
} from '~/common/types';
import { useAccessControl } from '~/common/hooks';
// eslint-disable-next-line @wyz/module/no-relative-import
import NavbarVerticalMenuItem from './navbar-vertical-menu-item';

const CollapseItems = ({ route }: CollapseItemsProps) => {
  const { pathname } = useLocation();

  const openCollapse = (children: Array<RouteDefinition<string>>) => {
    const checkLink = (child: RouteDefinition<string>) => {
      if (child.to === pathname) {
        return true;
      }
      return (
        Object.prototype.hasOwnProperty.call(children, 'children') &&
        child.children?.some(checkLink)
      );
    };
    return children.some(checkLink);
  };

  const [open, setOpen] = useState<boolean>(openCollapse(route.children || []));
  return (
    <Nav.Item as='li' className='has-children'>
      <Nav.Link
        onClick={() => {
          setOpen(!open);
        }}
        className={clsx('dropdown-indicator cursor-pointer')}
        aria-expanded={open}
        data-cy={route.dataCy}
      >
        <NavbarVerticalMenuItem route={route} />
      </Nav.Link>
      <Collapse in={open}>
        <Nav className='flex-column nav' as='ul'>
          {route.children && <NavbarVerticalMenu routes={route.children} />}
        </Nav>
      </Collapse>
    </Nav.Item>
  );
};

type CollapseItemsProps = {
  route: RouteDefinition<keyof TranslationKeys>;
};

type NavbarVerticalMenuProps = {
  routes: SideBarRoute<keyof TranslationKeys>['children'];
};
function isSeparator<T>(arg: unknown): arg is SeparatorDefinition<T> {
  return isObject(arg) && 'separator' in arg && arg.separator === true;
}
const NavbarVerticalMenu = ({ routes }: NavbarVerticalMenuProps) => {
  const { showBurgerMenu, isNavbarVerticalCollapsed, setConfig } = useTheme();
  const handleNavItemClick = () => {
    if (showBurgerMenu) {
      setConfig('showBurgerMenu', !showBurgerMenu);
      setConfig('isNavbarVerticalCollapsed', !isNavbarVerticalCollapsed);
    }
  };

  const acl = useAccessControl();

  const canUserAccess = (
    route: SideBarRoute<keyof TranslationKeys>['children'][number],
  ): boolean => {
    if ('children' in route && !isNil(route.children)) {
      return route.children.some(canUserAccess);
    }

    if (!isNil(route.features) && !acl.isEnabledFeatures(route.features)) {
      return false;
    }

    if (isNil(route.roles)) {
      return true;
    }
    return acl.isGranted(route.roles, 'affirmative');
  };
  return (
    <>
      {routes.map((route) => {
        if (!canUserAccess(route))
          return <Fragment key={`${route.name}.separator`} />;
        if (isSeparator(route)) {
          return (
            <li key={`${route.name}.separator`} className='nav-item'>
              <div className='mt-3 mb-2 navbar-vertical-label-wrapper row'>
                <div className='navbar-vertical-label navbar-vertical-label col-auto'>
                  <Trans i18nKey={route.name} />
                </div>
                <div className='ps-0 col'>
                  <hr className='mb-0 navbar-vertical-divider' />
                </div>
              </div>
            </li>
          );
        }
        if (!route.children) {
          return (
            <Nav.Item as='li' key={route.name} onClick={handleNavItemClick}>
              <NavLink
                end={route.exact}
                to={route.to}
                className={({ isActive }) =>
                  clsx('nav-link', {
                    active: isActive,
                  })
                }
                data-cy={route.dataCy}
                target={route.externalTarget ? '_blank' : undefined}
              >
                <NavbarVerticalMenuItem route={route} />
              </NavLink>
            </Nav.Item>
          );
        }
        return <CollapseItems route={route} key={route.name} />;
      })}
    </>
  );
};

export default NavbarVerticalMenu;
